import * as React from 'react';
import { NavLink, withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { Plus, LogOut, LucideIcon } from 'lucide-react';

import { TipsterProfileCard } from './TipsterProfileCard';
import NewTcLogo from '../../assets/NewTcLogo';
// Custom SVG icons
import { ReactComponent as HomeIcon } from '../../assets/home.svg';
import { ReactComponent as TipsIcon } from '../../assets/tips.svg';
import { ReactComponent as SalesIcon } from '../../assets/sales.svg';
import { ReactComponent as BalanceIcon } from '../../assets/balance.svg';
import { tr } from '../../utils/translations/translator';
import { RootState } from '../../store';
import { ownedBetsActions } from '../../store/ownedBets/actions';
import { UnlockedTipData } from '../../store/tips/reducer';
import { useFeatureFlagContext } from '../__providers__/FeatureFlagsContext';
import { useAssistantMode } from '../../hooks/useAssistantMode';

// Type for icon - can be Lucide component, SVG string, or image URL
type IconType = LucideIcon | string | React.ComponentType<any>;

interface RouteConfig {
  key: string;
  active: boolean;
  path: string;
  icon?: IconType;
  badge?: number | string;
  onClick?: () => void;
  assistantHidden?: boolean;
}

interface SectionConfig {
  key: string;
  routes: RouteConfig[];
}

export interface StateProps {
  userImg: string;
  openData: { tips: UnlockedTipData[]; matches: any };
  closedData: { tips: UnlockedTipData[]; matches: any };
  requestLogout: () => void;
  user: any;
  setIsOpen: (bol: boolean) => void;
  isOpen: boolean;
}

export interface DispatchProps {
  getBets: (openstatus: any) => any;
}

type TipsterSidebarProps = StateProps & DispatchProps & RouteComponentProps;

// Icon renderer component
const SidebarIcon: React.FC<{
  icon: IconType;
  className?: string;
  isActive?: boolean;
}> = ({ icon, className = '', isActive = false }) => {
  const baseClasses = 'flex-shrink-0';
  const combinedClasses = `${baseClasses} ${className}`;

  // Handle both function components and objects (webpack module format)
  if (typeof icon === 'function' || typeof icon === 'object') {
    // If it's an object, it might be a webpack module with default export
    const IconComponent =
      typeof icon === 'object' && icon !== null && 'default' in icon
        ? (icon as any).default
        : (icon as any);

    // Check if it's a Lucide icon by checking if it's from lucide-react
    // Lucide icons are from 'lucide-react' and custom SVGs are local imports
    const isLucideIcon = IconComponent === Plus || IconComponent === LogOut;

    if (isLucideIcon) {
      // Lucide icon
      return (
        <IconComponent
          className={combinedClasses}
          strokeWidth={2}
          style={{ fill: 'none' }}
        />
      );
    } else {
      // Custom SVG component (from ReactComponent import)
      return (
        <IconComponent
          className={`${combinedClasses} ${
            isActive
              ? '[&_path]:fill-white'
              : '[&_path]:fill-gray-400 group-hover:[&_path]:fill-white'
          } transition-all`}
        />
      );
    }
  }

  // SVG string or image URL
  if (typeof icon === 'string') {
    // Check if it's an SVG string
    if (icon.includes('<svg')) {
      return (
        <div
          className={combinedClasses}
          dangerouslySetInnerHTML={{ __html: icon }}
        />
      );
    }
    // Otherwise treat as image URL
    return (
      <img
        src={icon}
        alt=""
        className={combinedClasses}
        style={{ filter: isActive ? 'brightness(0) invert(1)' : 'none' }}
      />
    );
  }

  return null;
};

export const TipsterSidebarNew: React.FC<TipsterSidebarProps> = ({
  userImg,
  requestLogout,
  user,
  getBets,
  openData,
  closedData,
  setIsOpen,
  isOpen,
  location,
}) => {
  const { t } = useTranslation();
  const { isEnabled } = useFeatureFlagContext();
  const { isAssistantMode } = useAssistantMode();

  React.useEffect(() => {
    getBets('open');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedData]);

  const isChannelsFlagEnabled = isEnabled('channel_based_tips_publishing');

  const isRouteActive = (routePath: string) => {
    return (
      location.pathname === routePath ||
      location.pathname.startsWith(routePath + '/')
    );
  };

  // Define sections with icons
  const sections: SectionConfig[] = [
    {
      key: 'home',
      routes: [
        {
          key: 'home',
          active: true,
          path: '/',
          icon: HomeIcon,
        },
      ],
    },
    {
      key: 'tips',
      routes: [
        {
          key: 'publish',
          active: true,
          path: '/pc',
          icon: Plus,
          onClick: !isChannelsFlagEnabled ? () => setIsOpen(true) : undefined,
        },
        {
          key: 'close',
          active: true,
          path: '/my-bets',
          icon: TipsIcon,
          badge: openData
            ? openData.tips.length >= 40
              ? '+40'
              : openData.tips.length
            : 0,
        },
      ],
    },
    {
      key: 'monetization',
      routes: [
        {
          key: 'sales',
          active: true,
          path: '/sales',
          icon: SalesIcon,
          assistantHidden: true,
        },
        {
          key: 'balance',
          active: true,
          path: '/balance',
          icon: BalanceIcon,
          assistantHidden: true,
        },
      ],
    },
  ];

  return (
    <div className="h-screen w-[280px] p-4 bg-gray-100">
      <div className="fixed flex h-[calc(100vh-2rem)] w-[264px] flex-col justify-between rounded-2xl bg-gradient-to-b from-[#23313b] to-[#121619] shadow-2xl border border-white/10">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center p-6 border-b border-white/10">
            <NewTcLogo className="h-10 w-auto" />
          </div>

          {/* User Profile */}
          <div className="px-4 py-4">
            <TipsterProfileCard userImg={userImg} username={user.username} />
          </div>

          {/* Navigation Routes */}
          <nav className="flex-1 overflow-y-auto px-3 py-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
            {sections.map((section) => (
              <div key={section.key} className="mb-6 first:mt-0">
                {/* Section Header */}
                <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  {tr(t, section.key)}
                </div>

                {/* Routes */}
                <div className="space-y-1">
                  {section.routes.map((route) => {
                    if (!route.active) return null;
                    if (route.assistantHidden && isAssistantMode) return null;

                    const isActive = isRouteActive(route.path);

                    // If route has onClick, render as button
                    if (route.onClick) {
                      return (
                        <button
                          key={route.key}
                          onClick={route.onClick}
                          className={`group flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] text-white shadow-lg shadow-[#6633ff]/20'
                              : 'text-gray-300 hover:bg-gray-100/5 hover:text-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {route.icon && (
                              <SidebarIcon
                                icon={route.icon}
                                className="h-5 w-5"
                                isActive={isActive}
                              />
                            )}
                            <span>{tr(t, route.key)}</span>
                          </div>
                          {route.badge !== undefined && (
                            <span className="ml-auto inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6633ff] px-2 text-xs font-bold text-white">
                              {route.badge}
                            </span>
                          )}
                        </button>
                      );
                    }

                    // Otherwise render as NavLink
                    return (
                      <NavLink
                        key={route.key}
                        to={route.path}
                        className={`group flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] text-white shadow-lg shadow-[#6633ff]/20'
                            : 'text-gray-300 hover:bg-gray-100/5 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {route.icon && (
                            <SidebarIcon
                              icon={route.icon}
                              className="h-5 w-5"
                              isActive={isActive}
                            />
                          )}
                          <span>{tr(t, route.key)}</span>
                        </div>
                        {route.badge !== undefined && (
                          <span className="ml-auto inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6633ff] px-2 text-xs font-bold text-white">
                            {route.badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Logout Button */}
          <div className="border-t border-white/10 p-4">
            <button
              onClick={requestLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-gray-300 transition-all hover:bg-gray-100/5 hover:text-white"
            >
              <LogOut className="h-5 w-5" strokeWidth={2} />
              <span>{tr(t, 'logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  openData: state.ownedBets.data.open,
  closedData: state.ownedBets.data.closed,
  loading: state.ownedBets.isLoading,
  error: state.ownedBets.error,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getBets: (openstatus: any) =>
    dispatch(ownedBetsActions.ownedBetsRequestAction(openstatus)),
});

export const TipsterSidebarNewConnected = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TipsterSidebarNew),
);
